import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Header from "../../../components/header/Header";
import ProfileLayout, { useToast } from "../../../components/profile/ProfileLayout";
import VerifyCard from "../../../components/profile/VerifyCard";
import Field from "../../../components/profile/Field";
import { IconFile, IconUpload, IconX, IconCheck } from "../../../components/profile/icons";
import { API, authHeaders, extractError, KYC, useKycStatus, useMe } from "../../../components/profile/useProfileApi";
import "./Kyc.css";

// value уходит на бэк как document_type — поэтому русские строки, как и раньше.
const DOC_TYPES = [
  { value: "Национальный паспорт", key: "nationalPassport", placeholder: "1234 123456" },
  { value: "Заграничный паспорт", key: "internationalPassport", placeholder: "70 1234567" },
  { value: "Водительское удостоверение", key: "driverLicense", placeholder: "70 70 123456" },
];

const formatSize = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} КБ` : `${(bytes / 1024 / 1024).toFixed(1)} МБ`;

/* ---------- плитка загрузки файла ---------- */
function FileDrop({ label, file, onChange, optional, error }) {
  const { t } = useTranslation();
  const [drag, setDrag] = useState(false);

  const preview = useMemo(
    () => (file && file.type && file.type.startsWith("image/") ? URL.createObjectURL(file) : null),
    [file]
  );
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  return (
    <div
      className={`fx-drop${file ? " has" : ""}${drag ? " drag" : ""}${error ? " err" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) onChange(f);
      }}
    >
      <input
        type="file"
        accept="image/*,.pdf"
        aria-label={label}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) onChange(f);
          e.target.value = ""; // чтобы можно было выбрать тот же файл повторно
        }}
      />

      {file ? (
        <>
          <span className="fx-drop-thumb">
            {preview ? <img src={preview} alt="" /> : <IconFile />}
          </span>
          <span className="fx-drop-title">{label}</span>
          <span className="fx-drop-file mono" title={file.name}>{file.name}</span>
          <span className="fx-drop-meta mono">
            <IconCheck /> {formatSize(file.size)}
          </span>
          <button
            type="button"
            className="fx-drop-x"
            aria-label={t("kyc.removeFile", "Убрать файл")}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            <IconX />
          </button>
        </>
      ) : (
        <>
          <span className="fx-drop-icon"><IconUpload /></span>
          <span className="fx-drop-title">{label}</span>
          <span className="fx-drop-meta">
            {error || t("kyc.dropHint", "Нажмите или перетащите файл")}
          </span>
          {/* {optional ? <span className="fx-drop-opt">{t("kyc.optional", "если есть")}</span> : null} */}
        </>
      )}
    </div>
  );
}

const Kyc = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const { status, refresh } = useKycStatus();
  const { toast, notify } = useToast();

  const [fullName, setFullName] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[0].value);
  const [series, setSeries] = useState("");
  const [files, setFiles] = useState({ selfie: null, front: null, back: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const placeholder = (DOC_TYPES.find((d) => d.value === docType) || DOC_TYPES[0]).placeholder;
  const required = t("profile.required", "Обязательное поле");

  const clearError = (k) => errors[k] && setErrors((er) => ({ ...er, [k]: undefined }));
  const setFile = (k) => (f) => {
    setFiles((s) => ({ ...s, [k]: f }));
    clearError(k);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // задняя сторона не обязательна: у загранпаспорта её нет
    const nextErrors = {};
    if (!fullName.trim()) nextErrors.fullName = required;
    if (!series.trim()) nextErrors.series = required;
    if (!files.selfie) nextErrors.selfie = required;
    if (!files.front) nextErrors.front = required;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      notify(`${t("kyc.aroseError")}: ${t("personalInfo.nullInputError")}`, "err");
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      // порядок как в прежней версии: обратная сторона, лицевая, селфи
      [files.back, files.front, files.selfie].forEach((f) => f && form.append("files", f));

      // Content-Type не задаём вручную — браузер сам добавит boundary
      await axios.post(`${API}/kyc/upload`, form, {
        headers: { accept: "application/json", ...authHeaders() },
      });
      await axios.post(
        `${API}/kyc/details`,
        { document_type: docType, passport_data: series, patronymic: fullName },
        { headers: authHeaders() }
      );
      notify(t("kyc.bidSand"), "ok");
      refresh();
    } catch (err) {
      notify(
        `${t("kyc.aroseError")}: ${extractError(err, t("profile.unknownError", "Неизвестная ошибка"))}`,
        "err"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const form = (
    <form onSubmit={handleSubmit} noValidate>
      <section className="fx-section" id="document">
        <div className="fx-section-head">
          <h2>{t("kyc.documentSection", "Данные документа")}</h2>
          <p>{t("kyc.documentHint", "Впишите данные так, как они указаны в документе.")}</p>
        </div>
        <div className="fx-fgrid">
          <Field full label={t("kyc.fullNamePlaceholder")} error={errors.fullName}>
            <input
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                clearError("fullName");
              }}
              aria-invalid={!!errors.fullName}
            />
          </Field>
          <Field select label={t("kyc.selectDocumentType")}>
            <select value={docType} onChange={(e) => setDocType(e.target.value)}>
              {DOC_TYPES.map((d) => (
                <option key={d.key} value={d.value}>
                  {t(`kyc.documentTypes.${d.key}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("kyc.seriesLabel", "Серия и номер")} error={errors.series}>
            <input
              className="mono"
              type="text"
              placeholder={placeholder}
              value={series}
              onChange={(e) => {
                setSeries(e.target.value);
                clearError("series");
              }}
              aria-invalid={!!errors.series}
            />
          </Field>
        </div>
      </section>

      <section className="fx-section" id="photos">
        <div className="fx-section-head">
          <h2>{t("kyc.photosSection", "Фотографии")}</h2>
          <p>{t("kyc.photosHint", "Документ целиком в кадре, все данные читаются, без бликов и обрезанных краёв.")}</p>
        </div>
        <div className="fx-drops">
          <FileDrop label={t("kyc.selfieLabel")} file={files.selfie} onChange={setFile("selfie")} error={errors.selfie} />
          <FileDrop label={t("kyc.frontDocumentLabel")} file={files.front} onChange={setFile("front")} error={errors.front} />
          <FileDrop label={t("kyc.backDocumentLabel")} file={files.back} onChange={setFile("back")} optional />
        </div>
      </section>

      <div className="fx-actions">
        <button type="submit" className="fx-btn fx-btn-primary" disabled={submitting}>
          {submitting ? t("kyc.submitting", "Отправляем…") : t("kyc.submitButton")}
        </button>
      </div>
    </form>
  );

  // если документы уже на проверке или проверка пройдена — форму прячем под раскрывашку
  const locked = status === KYC.PENDING || status === KYC.VERIFIED;

  return (
    <ProfileLayout
      page="kyc"
      header={<Header hasMenu2={true} />}
      me={me}
      kycStatus={status}
      toast={toast}
    >
      <div className="fx-head">
        <h1>{t("kyc.title")}</h1>
        <p>{t("kyc.subtitle", "Подтвердите личность: загрузите документ и селфи.")}</p>
      </div>

      <VerifyCard id="verify" status={status} />

      {locked ? (
        <details className="fx-redo">
          <summary>{t("kyc.resend", "Отправить документы заново")}</summary>
          {form}
        </details>
      ) : (
        form
      )}

      {/* TODO: тексты-заглушки. Замените на реальные или верните <BenefitsContainer /> */}
      <section className="fx-section" id="about">
        <div className="fx-section-head">
          <h2>{t("kyc.aboutTitle", "Зачем проходить верификацию")}</h2>
        </div>
        <ul className="fx-benefits">
          <li>
            <b>{t("kyc.benefit1Title", "Защита аккаунта")}</b>
            <p>{t("kyc.benefit1Text", "Подтверждённая личность помогает восстановить доступ и защищает средства от мошенников.")}</p>
          </li>
          <li>
            <b>{t("kyc.benefit2Title", "Больше возможностей")}</b>
            <p>{t("kyc.benefit2Text", "Часть функций и лимитов доступна только верифицированным пользователям.")}</p>
          </li>
        </ul>
      </section>
    </ProfileLayout>
  );
};

export default Kyc;
